import { Injectable } from '@angular/core';
import { Agent, AgentKeys } from '@ag-common-lib/lib/models/domain/agent.model';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { Agency, AgencyKeys } from '@ag-common-lib/lib/models/domain/agency.model';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { AgencyService } from 'ag-common-svc/lib/services/agency.service';

@Injectable({
  providedIn: 'root',
})
export class HierarchyValidationService {
  constructor(
    private agentSearchService: AgentSearchService,
    private agencyService: AgencyService,
  ) {}

  private detectCircularTeamLeadAssignment = async (agent: Agent | null, agentData: Partial<Agent>) => {
    const currentAgentId = agent?.[AgentKeys.p_agent_id] ?? agentData?.[AgentKeys.p_agent_id];
    const selectedTeamLeadId = agentData?.[AgentKeys.manager_id];

    if (!currentAgentId || !selectedTeamLeadId) {
      return null;
    }

    const path: string[] = [String(currentAgentId)];
    const visited = new Set<string>(path);
    let nextAgentId: string | null = String(selectedTeamLeadId);

    while (nextAgentId) {
      path.push(nextAgentId);

      if (nextAgentId === currentAgentId) {
        return path;
      }

      if (visited.has(nextAgentId)) {
        return path;
      }

      visited.add(nextAgentId);

      const nextAgent = await this.agentSearchService.getByAgentId(nextAgentId);
      nextAgentId = nextAgent?.[AgentKeys.manager_id] ? String(nextAgent[AgentKeys.manager_id]) : null;
    }

    return null;
  };

  validateHierarchy = async (agent: Agent, updates: Partial<Agent>) => {
    const agentData = Object.assign({}, agent, updates);
    const mgaId = agentData?.[AgentKeys.p_mga_id];
    const agencyId = agentData?.[AgentKeys.p_agency_id];
    const teamLeadId = agentData?.[AgentKeys.manager_id];
    const agentId = agentData?.[AgentKeys.p_agent_id];

    if (agencyId && !mgaId) {
      throw new Error("Can't assign Agency Id without MGA Id");
    }

    const promises = [];
    const initialMgaId = agent?.[AgentKeys.p_mga_id];
    const initialAgencyId = agent?.[AgentKeys.p_agency_id];
    const initialAgentId = agent?.[AgentKeys.p_agent_id];
    const initialTeamLeadId = agent?.[AgentKeys.manager_id];
    const isTeamLeadChanged = (!!initialTeamLeadId || !!teamLeadId) && initialTeamLeadId !== teamLeadId;
    const isAgentIdChanged = (!!initialAgentId || !!agentId) && initialAgentId !== agentId;
    const isMgaChanged = (!!initialMgaId || !!mgaId) && initialMgaId !== mgaId;
    const isAgencyChanged = (!!initialAgencyId || !!agencyId) && initialAgencyId !== agencyId;
    const isWasTeamLead = agent?.[AgentKeys.is_manager] && !agentData?.[AgentKeys.is_manager];
    const needToCheckTeam = initialAgentId && initialMgaId && (isWasTeamLead || isMgaChanged || isAgentIdChanged);

    if (needToCheckTeam) {
      // Direct reports only, which is what the team check needs — /team walks the whole
      // downline at any depth.
      promises.push(
        this.agentSearchService
          .search({ filter: [AgentKeys.manager_id, '=', initialAgentId] })
          .then(response => ({ team: response?.data ?? [] })),
      );
    }

    if (isAgencyChanged && agencyId) {
      promises.push(this.agencyService.getAgencyByAgencyId(agencyId).then(agency => ({ agency })));
    }

    if (isMgaChanged && mgaId) {
      promises.push(this.agencyService.getAgencyByAgencyId(mgaId).then(mga => ({ mga })));
    }

    if (isAgentIdChanged && agentId) {
      promises.push(
        this.agentSearchService
          .getAgentsByAgentId(agentId)
          .then(agentsWithSameAgentId => ({ agentsWithSameAgentId })),
      );
    }

    if (isTeamLeadChanged && teamLeadId) {
      promises.push(this.agentSearchService.getByAgentId(teamLeadId).then(teamLead => ({ teamLead })));
    }

    const data = await Promise.all(promises).then(response => {
      return response?.filter(Boolean)?.reduce((acc, item) => {
        return Object.assign({}, acc, item);
      }, {});
    });

    const issues = [];

    if (isAgencyChanged && agencyId && !data?.agency) {
      issues.push(`Agency with id ${agencyId} not found.`);
    }

    if (isMgaChanged && mgaId && !data?.mga) {
      issues.push(`MGA with id ${mgaId} not found.`);
    }

    if (needToCheckTeam && data?.team?.length) {
      issues.push(`Changes affect hierarchy, need to re-assign teammates.`);
    }

    if ((isAgencyChanged || isMgaChanged) && !!data?.agency) {
      const mga: Agency = data?.mga;
      const agency: Agency = data?.agency;
      // MGA is fetched only when its value changes. When just the Agency changes,
      // validate it against the resulting agent MGA instead of treating the
      // intentionally-unfetched MGA document as a missing selection.
      const resultingMgaId = mga?.[AgencyKeys.agencyId] ?? agentData?.[AgentKeys.p_mga_id];
      const agencyId = agency?.[AgencyKeys.agencyId];
      const agencyMGA = agency?.[AgencyKeys.mga];

      agencyMGA !== resultingMgaId && resultingMgaId !== agencyId && issues.push(`Agency not belong MGA.`);
      !!agencyId && !resultingMgaId && issues.push(`MGA need to be specified.`);
    }

    if (isAgentIdChanged && data?.agentsWithSameAgentId?.length) {
      !!agent &&
        data?.agentsWithSameAgentId?.some(
          agentWithSameAgentId => agentWithSameAgentId?.[BaseModelKeys.dbId] !== agent?.[BaseModelKeys.dbId],
        ) &&
        issues.push(`Agent Id already assigned.`);
    }

    if ((isMgaChanged || isTeamLeadChanged) && !!data?.teamLead) {
      const teamLead = data?.teamLead;

      teamLead?.[AgentKeys.p_mga_id] !== mgaId && issues.push(`Team Lead not in same MGA.`);
      !teamLead?.[AgentKeys.is_manager] && issues.push(`Selected Team Lead is not allowed to be lead.`);
    }
    if (isTeamLeadChanged && !!teamLeadId && !data?.teamLead) {
      issues.push(`Team Lead with "${teamLeadId}" Id not exist.`);
    }

    if (isTeamLeadChanged && !!teamLeadId) {
      const circularPath = await this.detectCircularTeamLeadAssignment(agent, agentData);
      if (circularPath?.length) {
        const readablePath = circularPath.join(' -> ');
        const blockingAgentId = circularPath[circularPath.length - 2] ?? teamLeadId;
        issues.push(
          `Circular Team Lead Assignment: ${readablePath}. Reassign Team Lead for "${blockingAgentId}" (or another agent in this chain) before saving.`,
        );
      }
    }

    return { issues };
  };
}
